import { formatDate } from '@angular/common';
import { Component, OnInit, Input, OnChanges } from '@angular/core';
import { SettingsService } from 'src/app/services/settings.service';
import { UserService } from 'src/app/services/user.service';
import { TransactionDataBundle } from 'src/app/shared/models/transactions/transaction-data-bundle';
import { UserFiaPins } from 'src/app/shared/models/users/user-fia-pins';
import { UserProfile } from 'src/app/shared/models/users/user-profile';
import { Error } from 'src/app/shared/models/general/error';
import { SarsConfig } from 'src/app/shared/models/transactions/sars-config';

@Component({
  selector: 'app-stats-card',
  templateUrl: './stats-card.component.html',
  styleUrls: ['./stats-card.component.scss'],
})
export class StatsCardComponent implements OnInit, OnChanges {
  @Input() transactionDataBundle: TransactionDataBundle[] = [];
  @Input() fiaAvailable: number;
  @Input() sdaAvailable: number;
  @Input() busy: boolean = true;
  @Input() userProfile: UserProfile;

  sarsConfig: SarsConfig = new SarsConfig();
  sarsSdaLabel: string = '';
  sarsSdaValue: string = '';
  sarsFiaLabel: string = '';
  sarsFiaValue: string = '';
  totalMonthProfit: number = 0;
  totalYearProfit: number = 0;
  fiaTradedLabel: string = '';
  fiaTradedValue: string = '';
  fiaAvailableLabel: string = '';
  fiaAvailableValue: string = '';
  fiaUsedMessage: string = '';
  fiaUsedPercentage: number = 0;
  fiaUsed: number = 0;
  adminFiaLabel: string = '';
  adminFiaValue: string = '';
  fiaConfigError: boolean = false;
  sdaTradedLabel: string = '';
  sdaTradedValue: string = '';
  sdaRemainingMessage: string = '';
  sdaRemainingPercentage: number = 0;
  sdaUsed: number = 0;
  sdaTotalLabel: string = '';
  sdaTotalValue: string = '';
  userSdaExplination: string = '';
  adminSdaLabel: string = '';
  adminSdaValue: string = '';
  sdaConfigError: boolean = false;

  today = formatDate(new Date(), 'HH:mm a', 'en');
  month = formatDate(new Date(), 'MMMM', 'en');
  year = formatDate(new Date(), 'yyyy', 'en');

  showFiaPinsModal: boolean = false;
  userFiaPins: UserFiaPins[] = [];
  totalAITAvailable: number = 0;
  errorMsgFiaPin: string = '';

  constructor(
    public settingsService: SettingsService,
    private userService: UserService
  ) {
    this.busy = true;
  }

  ngOnInit() {}

  ngOnChanges() {
    this.CalcSDAFIAPercentages();
    this.CalcTotals();
    this.GetFiaPins();
  }

  GetFiaPins() {
    this.errorMsgFiaPin = '';
    this.userService.GetUsersFiaPins(this.userProfile.userId, true).subscribe({
      next: (fiaPins) => {
        this.userFiaPins = fiaPins;
        this.totalAITAvailable = UserFiaPins.calculatePinAvailableAmount(this.userFiaPins);
      },
      error: (error) => {
        this.errorMsgFiaPin = `Error encountered: [${Error.InterpretErrorMessage(error)}]`;
      },
    });
  }

  CalcSDAFIAPercentages() {
    this.fiaConfigError = false;
    this.sdaConfigError = false;

    const stats = this.transactionDataBundle[0]?.tradingStatistics;
    if (!stats) {
      return;
    }

    this.sarsConfig = stats.sarsConfig;

    //Fia comparison
    if (!this.sarsConfig?.fia) {
      this.fiaConfigError = true;
    } else {
      this.sarsFiaLabel = 'FIA';
      this.sarsFiaValue = this.sarsConfig.fia.toLocaleString();
      this.fiaUsed = this.sarsConfig.fia - this.fiaAvailable - (stats.adminFiaAmountReserved > 0 ? stats.adminFiaAmountReserved : 0);
      const fiaRemaining = this.sarsConfig.fia - this.fiaAvailable;
      this.fiaUsedPercentage = 100 - Math.round((fiaRemaining / this.sarsConfig.fia) * 100);
      this.fiaUsedMessage = this.fiaUsedPercentage.toString() + '%';

      this.fiaTradedLabel = 'Traded';
      this.fiaTradedValue = '- ' + stats.totalFiaTraded.toLocaleString();

      this.adminFiaLabel = 'Reserved';
      this.adminFiaValue = '- ' + stats.adminFiaAmountReserved.toLocaleString();

      this.fiaAvailableLabel = 'Available';
      this.fiaAvailableValue = this.fiaAvailable.toLocaleString();
    }

    //Sda comparison
    if (!this.sarsConfig?.sda) {
      this.sdaConfigError = true;
    } else {
      this.sarsSdaLabel = 'SDA';
      this.sarsSdaValue = this.sarsConfig.sda.toLocaleString();

      this.sdaTradedLabel = 'Traded';
      this.sdaTradedValue = '- ' + stats.totalSdaTraded.toLocaleString();

      this.sdaUsed = this.sarsConfig.sda - this.sdaAvailable - (stats.adminSdaAmountReserved > 0 ? stats.adminSdaAmountReserved : 0);
      const sdaRemaining = this.sarsConfig.sda - this.sdaAvailable;
      this.sdaRemainingPercentage = 100 - Math.round((sdaRemaining / this.sarsConfig.sda) * 100);
      this.sdaRemainingMessage = this.sdaRemainingPercentage.toString() + '%';

      this.adminSdaLabel = 'Reserved';
      this.adminSdaValue = '- ' + stats.adminSdaAmountReserved.toLocaleString();

      this.sdaTotalLabel = 'Available';
      this.sdaTotalValue = stats.sdaAvailable.toLocaleString();
    }
  }

  CalcTotals() {
    if (this.transactionDataBundle.length > 0) {
      this.totalMonthProfit = this.transactionDataBundle[new Date().getMonth()].totalTradeProfit;
      this.transactionDataBundle.forEach((x) => {
        if (x.totalTradeProfit) {
          this.totalYearProfit += x.totalTradeProfit;
        }
      });
      this.busy = false;
    }
  }

  relDiff(a: number, b: number) {
    var res = (b - a) / a;
    res = res * 100;
    if (res < 0) {
      res = res * -1;
    }
    return Math.round(res);
  }

  showFiaPins() {
    this.showFiaPinsModal = true;
  }
}
